import React, { ChangeEvent, useEffect, useState } from "react";
import { ImageUploadUtil } from "../../../APIs/index";
import { Modal } from "antd";

import HeaderTable from "../HeaderTable/HeaderTable";
import WaterMachineTable from "./WaterMachineTable/WaterMachineTable";
import { WaterMachine } from "../../../@types/productInfo";
import waterMachineServiceInstance from "../../../APIs/watermachines";
import WaterMachineModal from "../../Modal/WaterMachine/WaterMachineModal";
import { useNotification } from "../../Toast/Notification";
import { usePagination } from "../../../hooks/use-pagination";
import useSearchItem from "../../../hooks/use-search-item";

const Watermachines: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const productsPerPage = 3;

  const [name, setName] = useState<string>("");
  const [description, setDescription] = useState<string>("");
  const [meta_title, setMetaTitle] = useState<string>("");
  const [meta_description, setMetaDescription] = useState<string>("");
  const [video, setVideo] = useState<string>("");
  const [images_black, setimages_black] = useState<string[]>([]);
  const [images_white, setimages_white] = useState<string[]>([]);

  const [selectedRole, setSelectedRole] = useState("all");
  const [selectedMachine, setSelectedMachine] = useState<WaterMachine | null>(
    null
  );
  const [machinelink, setMachineLink] = useState("");
  const [watermachines, setWatermachines] = useState<WaterMachine[]>([]);

  const [searchTerm, setSearchTerm] = useState("");
  const [filteredMachines, setFilteredMachines] = useState<WaterMachine[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { notify } = useNotification();

  const [price, setPrice] = useState<string>("");
  const [sku, setSku] = useState<string>("");
  const [height, setHeight] = useState<string>("");
  const [width, setWidth] = useState<string>("");
  const [length, setLength] = useState<string>("");
  const [weight, setWeight] = useState<string>("");
  const [is_new, setIsNew] = useState<boolean>(false);

  const [isUpdating, setIsUpdating] = useState(false);

  const handleButtonClick = () => {
    setIsModalVisible(true);
    setIsUpdating(false);
  };

  const handleModalCancel = () => {
    setName("");
    setDescription("");
    setMetaTitle("");
    setMetaDescription("");
    setVideo("");
    setimages_black([]);
    setimages_white([]);
    setSelectedRole("all");
    setSelectedMachine(null);
    setMachineLink("");

    setIsUpdating(false);
    setIsModalVisible(false);
    setIsNew(false);

    setPrice("");
    setSku("");
    setHeight("");
    setWidth("");
    setLength("");
    setWeight("");
  };

  const handleFormSubmit = async () => {
    setIsLoading(true);

    const machineData: WaterMachine = {
      name,
      description,
      meta_title,
      meta_description,
      images_white: images_white,
      images_black: images_black,
      video,
      machinelink,
      height,
      weight,
      length,
      width,
      price,
      sku,
      is_new,
    };

    try {
      const { data, error } =
        await waterMachineServiceInstance.createWaterMachine(machineData);

      if (data) {
        notify("Machine created successfully", "success");
        fetchMachineData();

        handleModalCancel();
        setSelectedMachine(null);
        setTimeout(() => {}, 3000);
        setName("");
        setDescription("");
      } else {
        console.error("Error creating machine:", error);
        notify("Error creating machine", "error");
      }
    } catch (error) {
      console.error("Error creating machine:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditProduct = (machine: WaterMachine) => {
    setSelectedMachine(machine);

    setName(machine.name || "");
    setDescription(machine.description || "");
    setMetaTitle(machine.meta_title);
    setMetaDescription(machine.meta_description);
    setVideo(machine.video || "");
    setimages_black(machine.images_black || "");
    setimages_white(machine.images_white || "");
    setMachineLink(machine.machinelink);
    setPrice(machine.price || "");

    setIsUpdating(true);
    setIsModalVisible(true);
    setSku(machine.sku || "");
    setHeight(machine.height || "");
    setWidth(machine.width || "");
    setLength(machine.length || "");
    setWeight(machine.weight || "");
    setIsNew(machine.is_new || false);
  };

  //edit machine
  const handleSaveEdit = async () => {
    try {
      const machineId = selectedMachine?.id;

      if (machineId) {
        const updatedMachineData: WaterMachine = {
          name,
          description,
          meta_title,
          meta_description,
          images_white,
          images_black,
          video,
          machinelink,
          height,
          weight,
          length,
          width,
          price,
          sku,
          is_new,
        };

        const { data, error } =
          await waterMachineServiceInstance.editWaterMachine(
            machineId,
            updatedMachineData
          );

        if (data) {
          fetchMachineData();

          handleModalCancel();

          setSelectedMachine(null);
          setTimeout(() => {}, 3000);

          notify("Machine successfully Updated", "success");
        } else {
          console.error("Error updating machine:", error);
          notify("Error updating machine", "error");
        }
      } else {
        console.error("Selected machine or machine id is undefined");
      }
    } catch (error) {
      console.error("Error uploading images or updating machine:", error);
    } finally {
    }
  };

  //create the method to fetch all machine
  const fetchMachineData = async () => {
    try {
      const { data, error } =
        await waterMachineServiceInstance.getAllWaterMachines();

      if (data) {
        setWatermachines(data);
        setFilteredMachines(data);
      } else {
        console.error("Error fetching machines:", error);
      }
    } catch (error) {
      console.error("Error fetching machines:", error);
    }
  };

  useEffect(() => {
    fetchMachineData();
  }, []);

  const handleDeleteAcc = async (machineId: number) => {
    Modal.confirm({
      title: "Are you sure you want to delete this item?",
      content: "This action cannot be undone.",
      okText: "Yes",
      okType: "danger",
      cancelText: "No",
      onOk: async () => {
        if (machineId) {
          try {
            const { data, error } =
              await waterMachineServiceInstance.deleteWaterMachine(machineId);

            if (data) {
              setWatermachines((prevMachine) =>
                prevMachine.filter((machine) => machine.id !== machineId)
              );

              notify("Water Machine deleted successfully ", "success");

              fetchMachineData();

              handleModalCancel();
              setIsModalVisible(false);
              setSelectedMachine(null);
              setTimeout(() => {}, 3000);
            } else {
              console.error("Error deleting water machine:", error);
              notify("Error deleting water machine ", "error");
            }
          } catch (error) {
            console.error("Error deleting water machine:", error);
          } finally {
          }
        }
      },
    });
  };

  const handleFileChange = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = e.target.files;

    if (files && files.length > 0) {
      try {
        const uploadedImages = await Promise.all(
          Array.from(files).map(async (file) => {
            const uploadedImage = await ImageUploadUtil.uploadImage(file);
            if (uploadedImage.data) {
              return uploadedImage.data;
            } else {
              console.error("Error uploading image:", uploadedImage.error);
              return null;
            }
          })
        );

        const validImages = uploadedImages.filter(
          (image): image is string => image !== null
        );
        setimages_black((prevImages) => [...prevImages, ...validImages]);
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };
  const handleFileChangeImage = async (
    e: ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    const files = e.target.files;

    if (files && files.length > 0) {
      try {
        const uploadedImages = await Promise.all(
          Array.from(files).map(async (file) => {
            const uploadedImage = await ImageUploadUtil.uploadImage(file);
            if (uploadedImage.data) {
              return uploadedImage.data;
            } else {
              console.error("Error uploading image:", uploadedImage.error);
              return null;
            }
          })
        );

        const validImages = uploadedImages.filter(
          (image): image is string => image !== null
        );
        setimages_white((prevImages) => [...prevImages, ...validImages]);
      } catch (error) {
        console.error("Error uploading image:", error);
      }
    }
  };

  const handleFileChangeVideo = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];

    if (file) {
      try {
        const uploadedImage = await ImageUploadUtil.uploadImage(file);

        if (uploadedImage.data) {
          setVideo(uploadedImage.data);
        } else {
          console.error("Error uploading video:", uploadedImage.error);
        }
      } catch (error) {
        console.error("Error uploading video:", error);
      }
    }
  };

  useEffect(() => {
    if (video) {
    }
  }, [video]);

  const handlePageChange = (pageNumber: any) => {
    setCurrentPage(pageNumber);
  };

  const filterProductsFromSearch = useSearchItem({
    items: watermachines,
    searchTerm,
    selectedRole,
    searchFields: [
      "name",
      "description",
      "meta_title",
      "meta_description",
      "machinelink",
      "sku",
    ],
  });

  useEffect(() => {
    if (searchTerm.length > 0) {
    } else {
      setFilteredMachines(watermachines);
    }
  }, [watermachines, filterProductsFromSearch, searchTerm]);

  const totalPages = Math.ceil(filteredMachines.length / productsPerPage);
  const indexOfLastProduct = currentPage * productsPerPage;
  const indexOfFirstProduct = indexOfLastProduct - productsPerPage;
  const currentProducts = filteredMachines.slice(
    indexOfFirstProduct,
    indexOfLastProduct
  );

  const pageNumbers = usePagination(totalPages);

  const handleSearchTermChange = (e: any) => {
    setSearchTerm(e.target.value);
  };

  const handleRoleChange = (event: any) => {
    setSelectedRole(event.target.value);
  };

  return (
    <div className="p-6">
      <div className="hidden sm:block  py-2">
        <div className="text-center py-2">
          <p
            className="mb-[10px] box-border font-semibold 
            text-[rgb(0,34,56)] block text-[35px] text-left h-auto
            leading-[38.4px]">
            Water Machines
          </p>
        </div>
        <div className="bg-white p-4 rounded-md w-5/4 mx-auto">
          <HeaderTable
            subtitle="Manage Water Machines"
            buttonText="Add New Machine"
            inputPlaceholder="Search Accessory"
            selectPlaceholder="Filter By Product Type"
            loading={false}
            searchTerm={searchTerm}
            // selectedValue={selectedRole}
            onButtonClick={handleButtonClick}
            onSearchChange={handleSearchTermChange}
            onSelectChange={handleRoleChange}
            selectOptions={[
              { value: "flavour", label: "Flavours" },
              { value: "cylinders", label: "Cylinder" },
            ]}
          />
          <WaterMachineTable
            watermachines={filteredMachines}
            loading={isLoading}
            onEditOrder={handleEditProduct}
            currentPage={currentPage}
            handlePageChange={handlePageChange}
            pageNumbers={pageNumbers}
            currentProducts={currentProducts}
            handleDeleteAcc={handleDeleteAcc}
          />
        </div>
      </div>
      <WaterMachineModal
        handleFileChangeImage={handleFileChangeImage}
        isModalVisible={isModalVisible}
        onClose={handleModalCancel}
        handleFormSubmit={handleFormSubmit}
        isProcessing={isLoading}
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
        metaTitle={meta_title}
        setMetaTitle={setMetaTitle}
        metaDescription={meta_description}
        setMetaDescription={setMetaDescription}
        video={video}
        setVideo={setVideo}
        imagesBlack={images_black}
        setImagesBlack={setimages_black}
        imagesWhite={images_white}
        setImagesWhite={setimages_white}
        selectedRole={selectedRole}
        setSelectedRole={setSelectedRole}
        selectedMachine={selectedMachine}
        setSelectedMachine={setSelectedMachine}
        machineLink={machinelink}
        setMachineLink={setMachineLink}
        price={price}
        setPrice={setPrice}
        sku={sku}
        setSku={setSku}
        handleSaveEdit={handleSaveEdit}
        height={height}
        setHeight={setHeight}
        width={width}
        setWidth={setWidth}
        length={length}
        setLength={setLength}
        weight={weight}
        setWeight={setWeight}
        isUpdating={isUpdating}
        images_black={images_black}
        images_white={images_white}
        handleFileChange={handleFileChange}
        handleFileChangeVideo={handleFileChangeVideo}
        is_new={is_new}
        setIsnew={setIsNew}
      />
    </div>
  );
};

export default Watermachines;
